/**
 * Composable to get path without locale prefix
 * Removes locale prefix (e.g., '/ky/', '/en/') from route path
 * Useful for API calls that expect paths without locale prefixes
 */
export const useLocalizedPath = () => {
  const { locale, defaultLocale, locales } = useI18n()
  const route = useRoute()

  /**
   * Get current route path without locale prefix
   * @returns Path without locale prefix (e.g., 'application' instead of '/ky/application')
   */
  const getPathWithoutLocale = (): string => {
    const path = route.path

    // If current locale is default, path has no prefix
    if (locale.value === defaultLocale) {
      return path.replace(/^\/+/, '')
    }

    // Remove locale prefix from path
    const localePrefix = `/${locale.value}`
    if (path.startsWith(localePrefix)) {
      return path.replace(localePrefix, '').replace(/^\/+/, '')
    }

    // Fallback: try to remove any known locale prefix
    for (const loc of locales.value) {
      if (typeof loc === 'object' && 'code' in loc) {
        const prefix = `/${loc.code}`
        if (path.startsWith(prefix)) {
          return path.replace(prefix, '').replace(/^\/+/, '')
        }
      }
    }

    return path.replace(/^\/+/, '')
  }

  /**
   * Get path with locale prefix if needed
   * @param path - The path to process (e.g., '/panel', 'credits/islamic', '/')
   * @returns Path with locale prefix for non-russian languages
   *
   * Examples:
   * - getPathWithLocale('/panel') -> '/panel' (for ru), '/en/panel' (for en)
   * - getPathWithLocale('credits/islamic') -> 'credits/islamic' (for ru), '/en/credits/islamic' (for en)
   * - getPathWithLocale('/') -> '/' (for ru), '/en/' (for en)
   */
  const getPathWithLocale = (path: string): string => {
    if (locale.value === defaultLocale) {
      return path
    }

    // Ensure path starts with / for non-default locales
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `/${locale.value}${normalizedPath}`
  }

  return {
    getPathWithoutLocale,
    getPathWithLocale,
    /**
     * Computed property for convenience
     */
    pathWithoutLocale: computed(() => getPathWithoutLocale())
  }
}
